const _ = require('lodash');
const hiveBee = require('hive-bee');
const { BehaviorSubject } = require('rxjs');
const moment = require('moment');
const crossFetch = require('cross-fetch');

// IMPORTANT: in order to permit applications to perform more processing on configuration,
// we are explicitely NOT linking this service to the ConfigService.

// TODO:
//  - Add the concept of state to _connection$ values.
//  - Add possibility to link to a config$ (not just ConfigService.config$, but any observable, where an app
//    could pipe it to switch configuration if needed)

// Number of core reactions to keep
const KEEP_LAST_CORE_REACTIONS = 10;

const ConnectionState  = Object.freeze({
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',

  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',

  CONNECTING_FROM_TOKEN: 'CONNECTING_FROM_TOKEN',
  CONNECTED_FROM_TOKEN: 'CONNECTED_FROM_TOKEN',
});

const _connection$ = new BehaviorSubject(undefined);
const _connectionState$ = new BehaviorSubject(ConnectionState.DISCONNECTED);

let _setBeeFn = _.identity;
let _clearBeeFn = _.identity;
let _loadBeeFn = () => {};

const setBee = (token, username, bee, context) => {
  _setBeeFn(token, username, bee, context);
  _connection$.next({ token, username, bee });
};

const clearBee = (context) => {
  _clearBeeFn(context);
  _connection$.next(undefined);
};

function setupCallbacks({
  setBeeFn = _.identity,
  clearBeeFn = _.identity,
  loadBeeFn = () => {},
}) {
  if (!_.isFunction(setBeeFn)) {
    throw new Error(`setBeeFn must be a function: ${typeof(setBeeFn)}`);
  }
  if (!_.isFunction(clearBeeFn)) {
    throw new Error(`clearBeeFn must be a function: ${typeof(clearBeeFn)}`);
  }
  if (!_.isFunction(loadBeeFn)) {
    throw new Error(`loadBeeFn must be a function: ${typeof(loadBeeFn)}`);
  }

  _setBeeFn = setBeeFn;
  _clearBeeFn = clearBeeFn;
  _loadBeeFn = loadBeeFn;
}

const handleReaction = (bee) => reaction => {
  bee.lastReactions.push(reaction);
  bee.lastReactions = _.takeRight(bee.lastReactions, KEEP_LAST_CORE_REACTIONS);
};

const clearCallbacks = (bee) => {
  if (bee) {
    const callbackIds = _.get(bee, 'callbackIds', []);
    _.forEach(callbackIds, bee.reactions.removeCallback);

    _.unset(bee, 'callbackIds');
    _.unset(bee, 'lastReactions');
  }
};

async function initBee(config, token) {
  try {
    console.debug('Initializing authenticated bee...');

    const host = _.get(config, 'host', 'http://localhost');
    hiveBee.urls.setUrls(host, host, host);
    const bee = hiveBee.init(token, config.orgId, config.appId);
    bee.on('reactions.error', (_ignored, error) => handleBeeError(error));
    bee.on('shell.error', error => handleBeeError(error));
    bee.token = token;

    // If specified, check if the connection user has the specified role(s)
    const rolesToCheck = _.compact(_.castArray(_.get(config, 'validation.rolesToCheck')));
    for (let roleToCheck of rolesToCheck) {
      const hasRole = await bee.auth.hasRole(roleToCheck);
      if (!hasRole) {
        throw new Error(`User does not have required role: ${rolesToCheck}`);
      }
    }

    // Keep the last few core and invocation reactions (needed for watiForReaction)
    bee.lastReactions = [];
    bee.callbackIds = [];
    bee.callbackIds.push(bee.reactions.setCallback('core', handleReaction(bee)));
    bee.callbackIds.push(bee.reactions.setCallback('invocation', handleReaction(bee)));

    // Subscribe to specified categories (from the configuration)
    const categories = _.get(config, 'categories', []);
    if (!_.isEmpty(categories)) {
      bee.subscriptionId = await bee.reactions.subscribe(categories);
    }

    console.debug('Initialized authenticated bee');
    return bee;
  } catch (error) {
    console.error(`Failed to initialize authenticated bee: ${error}`);
    throw error;
  }
}

async function releaseBee(bee) {
  console.debug('Releasing authenticated bee...');
  let handleConnectionState = false;

  if (!bee) {
    handleConnectionState = true;
    _connection$.subscribe(c => bee = _.get(c, 'bee')).unsubscribe();
  }

  if (bee) {
    clearCallbacks(bee);

    if (handleConnectionState) {
      _connectionState$.next(ConnectionState.DISCONNECTING);
    }

    try {
      if (bee.subscriptionId) {
        await bee.reactions.unsubscribe(bee.subscriptionId);
        bee.subscriptionId = undefined;
      }

      await bee.auth.revokeToken()
        .catch(error => console.error(`Could not revoke token:`, error));

      if (handleConnectionState) {
        _connectionState$.next(ConnectionState.DISCONNECTED);
      }

      console.debug('Released authenticated bee.');
    } catch (error) {
      console.error(`Failed to release authenticated bee: ${error}`);
      throw error;
    } finally {
      if (bee.clearConnection) {
        clearBee(bee.context || {});
      }
    }
  }
}

function newBee(authFn, config, username, password) {
  if (!username) {
    throw new Error('User name cannot be empty');
  }

  if (!password) {
    throw new Error('Password cannot be empty');
  }

  const host = _.get(config, 'host', 'http://localhost');
  const orgId = _.get(config, 'orgId');
  const appId = _.get(config, 'appId');
  hiveBee.urls.setUrls(host, host, host);
  return authFn(orgId, appId, username, password)
    .then(token => initBee(config, token));
}

async function signIn(
  authFn,
  config,
  username,
  password,
  context = {},
  connectionStates = {
    CONNECTING: ConnectionState.CONNECTING,
    CONNECTED:  ConnectionState.CONNECTED,
    DISCONNECTED: ConnectionState.DISCONNECTED,
  }
) {
  _connectionState$.next(connectionStates.CONNECTING);

  let bee;
  try {
    bee = await newBee(authFn, config, username, password);

    bee.context = context || {};
    bee.clearConnection = true;

    setBee(bee.token, username, bee, context);

    _connectionState$.next(connectionStates.CONNECTED);

    return bee;
  } catch (error) {
    clearCallbacks(bee);
    clearBee();
    _connectionState$.next(connectionStates.DISCONNECTED);
    throw error;
  }
}

function loadFromStorage(context) {
  // Check if we have a stored token, if so, we don't need the login screen
  const data = _loadBeeFn(context);
  const token = {
    access_token: _.get(data, 'token'),
    access_token_expiry: _.get(data, 'tokenExpiration'),
  };
  const username = _.get(data, 'username');
  const expiration = moment(token.access_token_expiry * 1000);
  const now = moment();

  if (token && expiration.isAfter(now)) {
    return { token, username };
  }

  throw new Error('No bee information available in storage');
}

function fetchTokenFromCode(config, code) {
  const host = _.get(config, 'host');
  const url = `${host}/oidc/code/${code}`;
  return crossFetch(url)
    .then(response => {
      if (response.status !== 200) {
        throw response.statusText;
      }

      return response.json();
    });
}

const devAuthFn = (orgId, _appId, username, password) => hiveBee.auth.dev(username, password, orgId);
const tokenAuthFn = (token) => () => Promise.resolve(token);

function signInAsUser(config, username, password, context = {}) {
  return signIn(hiveBee.auth.user, config, username, password, context);
}

function signInAsBee(config, username, password, context = {}) {
  return signIn(hiveBee.auth.bee, config, username, password, context);
}

function signInAsDev(config, username, password, context = {}) {
  return signIn(devAuthFn, config, username, password, context);
}

function signInWithToken(config, username, token, context = {}) {
  return signIn(
    tokenAuthFn(token),
    config,
    username,
    'ignored',
    context,
    {
      CONNECTING: ConnectionState.CONNECTING_FROM_TOKEN,
      CONNECTED:  ConnectionState.CONNECTED_FROM_TOKEN,
      DISCONNECTED: ConnectionState.DISCONNECTED,
    }
  );
}

async function signInFromStorage(config, context = {}) {
  console.log('Initializing authenticated bee from storage...');

  const { token, username } = loadFromStorage(context);
  const { bee } = await signInWithToken(config, username, token, context);

  console.log('Initialized authenticated bee from storage');
  return { bee };
}

async function signInFromOidcCode(config, code, context = {}) {
  console.log('Initializing authenticated bee from OIDC code...');

  const { token, username } = await fetchTokenFromCode(config, code);
  const { bee } = await signInWithToken(config, username, token, context);

  console.log('Initialized authenticated bee from OIDC code');
  return bee;
}

function newBeeAsUser(config, username, password) {
  return newBee(hiveBee.auth.user, config, username, password);
}

function newBeeAsBee(config, username, password) {
  return newBee(hiveBee.auth.bee, config, username, password);
}

function newBeeAsDev(config, username, password) {
  return newBee(devAuthFn, config, username, password);
}

function newBeeWithToken(config, username, token) {
  return newBee(tokenAuthFn(token), config, username, 'ignored');
}

async function newBeeFromStorage(config, context = {}) {
  console.log('Initializing authenticated bee from storage...');

  const { token, username } = loadFromStorage(context);
  const bee = await newBeeWithToken(config, username, token, context);

  console.log('Initialized authenticated bee from storage');
  return bee;
}

async function newBeeFromOidcCode(config, code, context = {}) {
  console.log('Initializing authenticated bee from OIDC code...');

  const { token, username } = await fetchTokenFromCode(config, code);
  const bee = await newBeeWithToken(config, username, token, context);

  console.log('Initialized authenticated bee from OIDC code');
  return bee;
}

function handleBeeError(error) {
  const status = _.get(error, 'status');
  if (status === 401 || status === 403) {
    console.error('Received error from Hive, disconnecting...', error);
    return releaseBee();
  }

  return Promise.resolve();
}

function generateOidcUrl(config, callbackUrl) {
  const host = _.get(config, 'host');
  if (!host) {
    throw new Error(`config object parameter must have 'host' property!`);
  }

  const appId = _.get(config, 'appId');
  if (!appId) {
    throw new Error(`config object parameter must have 'appId' property!`);
  }

  const orgName = _.get(config, 'orgName');
  if (!orgName) {
    throw new Error(`config object parameter must have 'orgName' property!`);
  }

  return `${host}/oidc/login/${orgName}/${appId}?callback=${callbackUrl}`;
}

function getConnection() {
  let connection;
  _connection$
    .subscribe(c => (connection = c))
    .unsubscribe();

  return connection;
}

function assertConnection() {
  const connection = getConnection();
  if (!connection) {
    throw new Error("Connection is needed!");
  }
  return connection;
}

/**
 * @namespace ConnectionService
 */
module.exports = {
  /**
   * @function setBeeFn
   * @memberof ConnectionService
   * @description Callback that is called when the observable connection is about to be set.
   *
   * @param {String} token - The token for the new connection
   * @param {String} username - The username for the new connection
   * @param {Object} bee - The bee that for the new connection
   * @param {Object} context - An application context that was passed to one of the signIn functions.
   *
   * @see {@link setupCallbacks} for further info.
   */

  /**
   * @function clearBeeFn
   * @memberof ConnectionService
   * @description Callback that is called when the observable connection is about to be cleared.
   *
   * @param {Object} context - An application context that was passed to one of the signIn functions.
   *
   * @see {@link setupCallbacks} for further info.
   */

  /**
   * @function loadBeeFn
   * @memberof ConnectionService
   * @description Callback that returns stored bee information
   *
   * @param {Object} context - An application context that was passed to one of the signIn functions.
   *
   * @returns {Object} data - an object that contains the stored data. Can be nil when there is no stored data
   * @returns {String} data.token - the stored token to used.
   * @returns {String} data.tokenExpiration - the stored token expiration
   * @returns {String} data.username - the stored username.
   *
   * @see {@link setupCallbacks} for further info.
   */

  /**
   * @enum ConnectionState
   * @memberof ConnectionService
   * @description The various states of the connection service.
   *
   * @property DISCONNECTED Indicates that the connection is not established.
   * @property DISCONNECTING Indicates that the connection is being torn down.
   * @property CONNECTING Indicates that the connection is being established.
   * @property CONNECTED Indicates that the connection is established.
   * @property CONNECTING_FROM_TOKEN Indicates that the connection is being established using a stored token.
   * @property CONNECTED_FROM_TOKEN Indicates that the connection is established from a stored token.
   */
  ConnectionState,

  /**
   * @constant connectionState$
   * @memberof ConnectionService
   * @description An observable that can be subscribed to in order to get updates about the status of the connection to
   * Hive. Refer to [RxJS Observable](http://reactivex.io/rxjs/class/es6/Observable.js~Observable.html) for more details.
   */
  connectionState$: _connectionState$,

  /**
   * @constant connection$
   * @memberof ConnectionService
   * @description An observable that can be subscribed to in order to get updates about the connection to Hive. Refer
   * to [RxJS Observable](http://reactivex.io/rxjs/class/es6/Observable.js~Observable.html) for more details.
   */
  connection$: _connection$,

  /**
   * @function setupCallbacks
   * @memberof ConnectionService
   * @description Sets up callback for connections when setBee and/or clearBee is called.
   *
   * @param {Object} callbacks - an object containing various callback functions
   * @param {setBeeFn} callbacks.setBeeFn - A function that is called when a bee is connected.
   * @param {clearBeeFn} callbacks.clearBeeFn - A function that is called when a bee is disconnected.
   * @param {loadBeeFn} callbacks.loadBeeFn - A function that is called when trying to sign-in with stored credentials
   *
   * @see {@link setBeeFn} for further info.
   * @see {@link clearBeeFn} for further info.
   * @see {@link loadBeeFn} for further info.
   */
  setupCallbacks,

  /**
   * @function signInAsUser
   * @memberof ConnectionService
   * @description Signs in to Hive as an application user. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} password - the application user password
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInAsUser,

  /**
   * @function signInAsBee
   * @memberof ConnectionService
   * @description Signs in to Hive as a bee user. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the bee user name
   * @param {String} password - the bee user password
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInAsBee,

  /**
   * @function signInAsDev
   * @memberof ConnectionService
   * @description Signs in to Hive as a dev user. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} password - the application user password
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInAsDev,

  /**
   * @function signInWithToken
   * @memberof ConnectionService
   * @description Signs in to Hive with an existing token. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} token - an existing token
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInWithToken,

  /**
   * @function signInFromStorage
   * @memberof ConnectionService
   * @description Signs in to Hive with stored credentials. **Important**: This call changes the connection observable
   * (connection$).
   *
   * This function uses the loadBeeFn callback to load credentials
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInFromStorage,

  /**
   * @function signInFromOidcCode
   * @memberof ConnectionService
   * @description Signs in to Hive with an OpenID Connect code. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {string} code - the OpenID Connect code that was obtained from the OIDC flow.
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the connection is established
   */
  signInFromOidcCode,

  /**
   * @function newBeeAsUser
   * @memberof ConnectionService
   * @description Connect to Hive as an application user. **Important**: This call does NOT change the connection
   * observable (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} password - the application user password
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeAsUser,

  /**
   * @function newBeeAsBee
   * @memberof ConnectionService
   * @description Connect to Hive as a bee user. **Important**: This call does NOT change the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the bee user name
   * @param {String} password - the bee user password
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeAsBee,

  /**
   * @function newBeeAsDev
   * @memberof ConnectionService
   * @description Connect to Hive as a dev user. **Important**: This call does NOT change the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} password - the application user password
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeAsDev,

  /**
   * @function newBeeWithToken
   * @memberof ConnectionService
   * @description Connect to Hive with an existing token. **Important**: This call does NOT change the connection
   * observable (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} config.validation - (Optional) an object with information about validating the connection
   * @param {String|Array} config.validation.rolesToCheck - (Optional) a role name or array or role names to check once
   * the bee is set-up. This validates that the token is valid.
   * @param {String} username - the application user name
   * @param {String} token - an existing token
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeWithToken,

  /**
   * @function newBeeFromStorage
   * @memberof ConnectionService
   * @description Connect to Hive with stored credentials. **Important**: This call changes the connection observable
   * (connection$).
   *
   * This function uses the loadBeeFn callback to load credentials
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeFromStorage,

  /**
   * @function newBeeFromOidcCode
   * @memberof ConnectionService
   * @description Connect to Hive with an OpenID Connect code. **Important**: This call changes the connection observable
   * (connection$).
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the base URL to the Hive instance to connect to
   * @param {String} config.orgId - the organization to connect to
   * @param {String} config.appId - the application to connect to
   * @param {Array} config.categories An array of categories and filters that the connection will subscribe to. Refer
   * to [Hive-Bee subscribe](https://docs.m21lab.com/hive-bee/bee.AuthenticatedBee.Reactions.html#.subscribe) for more
   * details.
   * @param {string} code - the OpenID Connect code that was obtained from the OIDC flow.
   * @param {Object} context - (optional) an application specific context for the connection. The context is passed to
   * the setBee/ClearBee callbacks.
   *
   * @returns {Promise} promise - a promise that resolves when the bee is connected
   */
  newBeeFromOidcCode,

  /**
   * @function releaseBee
   * @memberof ConnectionService
   * @description Releases any active connection.
   *
   * @param (Object) bee - A bee to release, if unspecified the saved connection be us used.
   *
   * @returns {Promise} promise - a promise that resolves when the bee is disconnected.
   */
  releaseBee,

  /**
   * @function handleBeeError
   * @memberof ConnectionService
   * @description Use to pass errors from API calls. The service decides if it needs to disconnect or not
   * @param {Object} - An error object, where it's status field is used to determine the need to disconnect or not.
   */
  handleBeeError,

  /**
   * @function generateOidcUrl
   * @memberof ConnectionService
   * @description Obtains the URL to use to perform the OpenID Connect flow for the given organization.
   *
   * @param {Object} config - a configuration object
   * @param {String} config.host - the Hive host to connect to
   * @param {String} config.orgName - the name of the organization. **Note**: the name and not the ID.
   * @param {String} config.appId - the application to connect to
   *
   * @returns {URL} - the URL to use to start the OpenID Connect flow.
   */
  generateOidcUrl,

  /**
   * @function getConnection
   * @memberof ConnectionService
   * @description Returns the current connection, if any.
   * @returns {Object} the current connection, can be null.
   */
  getConnection,

  /**
   * @function assertConnection
   * @memberof ConnectionService
   * @description Asserts that there is a current connection. If there is non, an exception is raised.
   *
   * @returns {Object} the current connection. If it returns, the connection can never be null.
   * @throws an an error if there is no connection.
   */
  assertConnection,
};
